/* eslint-disable max-lines */
/**
 * oelTokens.js — registries that the OEL editor uses for picker UX.
 *
 * Mirrors the runtime-side registry in Project-O-New `app/oel/builtins.py`
 * + the per-step output shapes derived from
 * `app/services/cpie/executor/<kind>_executor.py`. Keep in sync if the
 * runtime adds new functions or changes output shapes.
 */

/* eslint-disable no-template-curly-in-string */

export const OEL_FUNCTIONS = [
    {
        name: 'upper',
        sig: 'upper(s)',
        desc: 'Uppercase. None → ""',
        category: 'string',
        args: {
            min: 1,
            max: 1
        }
    },
    {
        name: 'lower',
        sig: 'lower(s)',
        desc: 'Lowercase. None → ""',
        category: 'string',
        args: {
            min: 1,
            max: 1
        }
    },
    {
        name: 'trim',
        sig: 'trim(s)',
        desc: 'Strip surrounding whitespace.',
        category: 'string',
        args: {
            min: 1,
            max: 1
        }
    },
    {
        name: 'startswith',
        sig: 'startswith(s, p)',
        desc: 'True if s begins with prefix p.',
        category: 'string',
        args: {
            min: 2,
            max: 2
        }
    },
    {
        name: 'endswith',
        sig: 'endswith(s, p)',
        desc: 'True if s ends with suffix p.',
        category: 'string',
        args: {
            min: 2,
            max: 2
        }
    },
    {
        name: 'replace',
        sig: 'replace(s, old, new)',
        desc: 'Replace every old with new in s.',
        category: 'string',
        args: {
            min: 3,
            max: 3
        }
    },
    {
        name: 'len',
        sig: 'len(x)',
        desc: 'Length of string/list/dict. None → 0.',
        category: 'collection',
        args: {
            min: 1,
            max: 1
        }
    },
    {
        name: 'first',
        sig: 'first(x)',
        desc: 'First element. Empty/None → null.',
        category: 'collection',
        args: {
            min: 1,
            max: 1
        }
    },
    {
        name: 'last',
        sig: 'last(x)',
        desc: 'Last element. Empty/None → null.',
        category: 'collection',
        args: {
            min: 1,
            max: 1
        }
    },
    {
        name: 'contains',
        sig: 'contains(x, v)',
        desc: 'Substring/element/key membership.',
        category: 'collection',
        args: {
            min: 2,
            max: 2
        }
    },
    {
        name: 'is_null',
        sig: 'is_null(x)',
        desc: 'True if x is null.',
        category: 'logic',
        args: {
            min: 1,
            max: 1
        }
    },
    {
        name: 'coalesce',
        sig: 'coalesce(a, b)',
        desc: 'a if a is not null, else b.',
        category: 'logic',
        args: {
            min: 2,
            max: 2
        }
    }
];

export const OEL_FUNCTION_INDEX = Object.fromEntries(OEL_FUNCTIONS.map((f) => [f.name, f]));

export const OEL_OPERATORS = [
    {
        token: '+',
        group: 'math',
        desc: 'Add / concat'
    },
    {
        token: '-',
        group: 'math',
        desc: 'Subtract'
    },
    {
        token: '*',
        group: 'math',
        desc: 'Multiply'
    },
    {
        token: '/',
        group: 'math',
        desc: 'Divide'
    },
    {
        token: '==',
        group: 'compare',
        desc: 'Equal'
    },
    {
        token: '!=',
        group: 'compare',
        desc: 'Not equal'
    },
    {
        token: '<',
        group: 'compare',
        desc: 'Less than'
    },
    {
        token: '>',
        group: 'compare',
        desc: 'Greater than'
    },
    {
        token: '<=',
        group: 'compare',
        desc: 'Less or equal'
    },
    {
        token: '>=',
        group: 'compare',
        desc: 'Greater or equal'
    },
    {
        token: 'and',
        group: 'logic',
        desc: 'Boolean and'
    },
    {
        token: 'or',
        group: 'logic',
        desc: 'Boolean or'
    },
    {
        token: 'not',
        group: 'logic',
        desc: 'Boolean not'
    }
];

export const OEL_CONSTANTS = [
    {
        token: 'true',
        desc: 'Boolean true'
    },
    {
        token: 'false',
        desc: 'Boolean false'
    },
    {
        token: 'null',
        desc: 'Null / missing'
    }
];

/**
 * Per-step-kind output shape — what `@<step_id>.…` paths are typed at build
 * time, before the step has actually run. Surfaced in the @ picker as
 * suggested completions. ALL kinds also expose `.status` and `.error`.
 *
 * Each entry: { path, type, desc }. `path` is appended after `@<step_id>`.
 */
export function outputPathsForStep(step, ctx) {
    if (!step || !step.kind) return [];
    const t = step.output_type;
    const llmPaths = [];
    if (!t || t === 'string') {
        llmPaths.push({
            path: '.output.result',
            type: 'string',
            desc: 'Generated text'
        });
    }
    llmPaths.push({
        path: '.output',
        type: t || 'string',
        desc: 'Full LLM output (typed by output_type)'
    });
    switch (step.kind) {
        case 'llm':
            return llmPaths;
        case 'rag':
            return [
                {
                    path: '.output.chunks',
                    type: 'array',
                    desc: 'Retrieved text chunks'
                },
                {
                    path: '.output.chunks[0].text',
                    type: 'string',
                    desc: 'First chunk text'
                },
                {
                    path: '.output.urls',
                    type: 'array',
                    desc: 'Source URLs'
                },
                {
                    path: '.output.data',
                    type: 'object',
                    desc: 'Raw RAG payload'
                }
            ];
        case 'api': {
            const base = [
                {
                    path: '.output.data',
                    type: 'object',
                    desc: 'Parsed response body'
                },
                {
                    path: '.output.status',
                    type: 'integer',
                    desc: 'HTTP status code'
                },
                {
                    path: '.output.headers',
                    type: 'object',
                    desc: 'Response headers'
                }
            ];
            // If we know the API's output_schema, drill into the body for nested keys.
            const reg = ctx?.apiSchemaRegistry || {};
            const outSchema = reg[step.api_id];
            if (outSchema && typeof outSchema === 'object') {
                const nested = walkJsonSchema(outSchema, '.output.data', 4);
                return base.concat(nested);
            }
            return base;
        }
        case 'memory':
            return [
                {
                    path: '.output.data',
                    type: 'any',
                    desc: 'Memory operation result'
                }
            ];
        case 'choose':
            return [
                {
                    path: '.output.chosen',
                    type: 'string',
                    desc: 'ID of chosen branch'
                },
                {
                    path: '.output.result',
                    type: 'any',
                    desc: 'Output of chosen branch'
                }
            ];
        case 'tool':
            return [
                {
                    path: '.output',
                    type: 'any',
                    desc: 'Tool result'
                },
                {
                    path: '.output.data',
                    type: 'any',
                    desc: 'Structured tool data'
                }
            ];
        case 'subflow':
            return [
                {
                    path: '.output',
                    type: 'object',
                    desc: 'Subflow output mapping'
                }
            ];
        case 'call':
            return [
                {
                    path: '.output',
                    type: 'object',
                    desc: 'Definition output'
                }
            ];
        case 'parallel':
            return [
                {
                    path: '.output',
                    type: 'object',
                    desc: 'All branch outputs (by branch id)'
                }
            ];
        default:
            return [{
                path: '.output',
                type: 'any',
                desc: 'Step output'
            }];
    }
}

/** Always-available paths on every step. */
export const STEP_META_PATHS = [
    {
        path: '.status',
        type: 'string',
        desc: '"SUCCESS" | "ERROR" | "RUNNING" | "SKIPPED" | "PAUSED"'
    },
    {
        path: '.error',
        type: 'string',
        desc: 'Error message (null on success)'
    }
];

/**
 * Flatten a JSON Schema into leaf path suggestions. Used for the API output
 * picker. Conservative: emits a row for every named leaf (and the parent
 * object/array nodes) up to `maxDepth` levels deep.
 */
export function walkJsonSchema(schema, basePath = '.output.data', maxDepth = 10) {
    const out = [];
    function visit(node, path, depth) {
        if (!node || typeof node !== 'object' || depth > maxDepth) return;
        const t = node.type || (node.properties ? 'object' : node.items ? 'array' : 'any');
        if (t === 'object' && node.properties) {
            // emit each named property
            for (const [k, sub] of Object.entries(node.properties)) {
                const subType = sub?.type || (sub?.properties ? 'object' : sub?.items ? 'array' : 'any');
                const desc = sub?.description ||
                    (sub?.format ? `format: ${sub.format}` : '') ||
                    (subType === 'object' ? 'object' : subType === 'array' ? 'array' : '');
                out.push({
                    path: `${path}.${k}`,
                    type: subType,
                    desc
                });
                visit(sub, `${path}.${k}`, depth + 1);
            }
        } else if (t === 'array' && node.items) {
            const subType = node.items.type || (node.items.properties ? 'object' : 'any');
            const desc = node.items.description || `${subType} element`;
            out.push({
                path: `${path}[0]`,
                type: subType,
                desc
            });
            visit(node.items, `${path}[0]`, depth + 1);
        }
    }
    visit(schema, basePath, 1);
    return out;
}
